import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center border px-2 h-6 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "rounded-[5px] border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "rounded-[5px] border-transparent bg-muted text-muted-foreground hover:bg-muted/80",
        destructive: "rounded-[5px] border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "rounded-[5px] text-foreground border-border-chip",
        statusPill: "rounded-full border-transparent gap-1.5 px-2.5 h-6 text-xs font-medium",
        roleTag: "rounded-[4px] border-transparent px-2 h-5 text-[11px] font-medium uppercase tracking-wide",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

// Status pill color mappings
type StatusType = "active" | "pending" | "error" | "open" | "info" | "success" | "warning";

const statusStyles: Record<StatusType, { dot: string; bg: string; text: string }> = {
  active: { dot: "bg-status-active", bg: "bg-status-active/[0.08]", text: "text-foreground" },
  pending: { dot: "bg-status-in-progress", bg: "bg-status-in-progress/[0.08]", text: "text-foreground" },
  error: { dot: "bg-status-cancelled", bg: "bg-status-cancelled/[0.08]", text: "text-foreground" },
  open: { dot: "bg-status-todo", bg: "bg-status-todo/[0.08]", text: "text-foreground" },
  info: { dot: "bg-status-info", bg: "bg-status-info/[0.08]", text: "text-foreground" },
  success: { dot: "bg-status-in-review", bg: "bg-status-in-review/[0.08]", text: "text-foreground" },
  warning: { dot: "bg-status-in-progress", bg: "bg-status-in-progress/[0.08]", text: "text-foreground" },
};

// Role tag color mappings
type RoleType = "user" | "agent" | "system" | "task";

const roleStyles: Record<RoleType, { bg: string; text: string }> = {
  user: { bg: "bg-muted-foreground/10", text: "text-muted-foreground" },
  agent: { bg: "bg-primary/10", text: "text-primary" },
  system: { bg: "bg-muted-foreground/10", text: "text-muted-foreground" },
  task: { bg: "bg-secondary-foreground/10", text: "text-secondary-foreground" },
};

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  status?: StatusType;
  role?: RoleType;
}

function Badge({ className, variant, status, role, children, ...props }: BadgeProps) {
  if (variant === "statusPill" && status) {
    const styles = statusStyles[status];
    return (
      <div className={cn(badgeVariants({ variant }), styles.bg, styles.text, className)} {...props}>
        <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", styles.dot)} />
        {children}
      </div>
    );
  }

  if (variant === "roleTag" && role) {
    const styles = roleStyles[role];
    return (
      <div className={cn(badgeVariants({ variant }), styles.bg, styles.text, className)} {...props}>
        {children || role.toUpperCase()}
      </div>
    );
  }

  return <div className={cn(badgeVariants({ variant }), className)} {...props}>{children}</div>;
}

export { Badge, badgeVariants };
export type { StatusType, RoleType };
